import { io, Socket } from 'socket.io-client';
import { authService } from './authService';

// Suppress WebSocket and Socket.IO errors in console (they're expected when using polling only)
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

const suppressSocketErrors = () => {
  console.error = (...args: any[]) => {
    const message = args.join(' ');
    // Suppress WebSocket and Socket.IO related errors
    if (message.includes('Invalid frame header') ||
      message.includes('WebSocket') ||
      message.includes('socket.io') ||
      message.includes('400') ||
      message.includes('BAD REQUEST') ||
      message.includes('polling') ||
      message.includes('transport')) {
      return; // Don't log these errors
    }
    originalConsoleError.apply(console, args);
  };
  console.warn = (...args: any[]) => {
    const message = args.join(' ');
    if (message.includes('socket') || message.includes('Socket')) {
      return;
    }
    originalConsoleWarn.apply(console, args);
  };
};

// Restore console.error after a delay to allow socket connection
const restoreConsoleError = () => {
  setTimeout(() => {
    console.error = originalConsoleError;
  }, 2000);
};

class SocketService {
  public socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  connect() {
    // Disconnect existing socket if any to avoid conflicts
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    const token = authService.getToken();
    if (!token) {
      // No token - don't try to connect
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      // Suppress Socket.IO errors during connection
      suppressSocketErrors();

      this.socket = io(apiUrl, {
        auth: {
          token: token
        },
        // Also send token in query string as fallback for polling handshake
        query: {
          token: token
        },
        // Use polling only to avoid WebSocket "Invalid frame header" errors
        // Polling is reliable and works well for most use cases
        transports: ['polling'],
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        forceNew: true, // Force new connection to avoid old session issues
        path: '/socket.io/',
        upgrade: false, // Disable upgrade since we're only using polling
        autoConnect: true,
      });

      // Restore console.error after connection attempt
      restoreConsoleError();
    } catch (error) {
      restoreConsoleError();
      return;
    }

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;

      const currentToken = authService.getToken();
      if (currentToken) {
        // Join user room
        this.socket?.emit('join_user_room', { token: currentToken });

        // Join community room
        this.socket?.emit('join_community', { token: currentToken });
      }

      // Re-setup all listeners after reconnect
      this.setupListeners();
    });

    // Setup listeners immediately
    this.setupListeners();

    this.socket.on('disconnect', (reason) => {
      // Socket disconnected
    });

    this.socket.on('connect_error', (error) => {
      // Silently ignore all socket connection errors
      // These errors are expected during session transitions (login/logout/refresh)
      const errorMessage = error.message || String(error);

      // If it's a 400 error (expired session), just disconnect quietly
      if (errorMessage.includes('400') || errorMessage.includes('BAD REQUEST')) {
        this.disconnect();
        return;
      }

      this.reconnectAttempts++;
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        // Max attempts reached - disconnect to stop further attempts
        this.disconnect();
      }
    });
  }

  private setupListeners() {
    if (!this.socket) return;

    // Remove existing listeners to avoid duplicates
    this.socket.off('new_notification');
    this.socket.off('unread_count');
    this.socket.off('new_post');
    this.socket.off('new_comment');
    this.socket.off('post_update');
    this.socket.off('new_message');
    this.socket.off('conversation_update');
    this.socket.off('user_status_update');
    this.socket.off('payment_success');

    // Listen for new notifications
    this.socket.on('new_notification', (notification) => {
      this.emit('new_notification', notification);
    });

    // Listen for unread count updates
    this.socket.on('unread_count', (data: { count: number }) => {
      this.emit('unread_count', data.count);
    });

    // Listen for new posts
    this.socket.on('new_post', (post) => {
      this.emit('new_post', post);
    });

    // Listen for new comments
    this.socket.on('new_comment', (data) => {
      this.emit('new_comment', data);
    });

    // Listen for post updates
    this.socket.on('post_update', (post) => {
      this.emit('post_update', post);
    });

    // Listen for new messages - THIS IS CRITICAL FOR CHAT
    this.socket.on('new_message', (message) => {
      this.emit('new_message', message);
    });

    // Listen for conversation updates
    this.socket.on('conversation_update', (conversation) => {
      this.emit('conversation_update', conversation);
    });

    // Listen for user status updates
    this.socket.on('user_status_update', (data) => {
      this.emit('user_status_update', data);
    });

    this.socket.on('payment_success', (order) => {
      this.emit('payment_success', order);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
  }

  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);

    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  private emit(event: string, data: any) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} callback:`, error);
        }
      });
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export const socketService = new SocketService();

